/**
 * app.jsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import './polyfills';

// Import all the third party stuff
import React from 'react';
import { createRoot } from 'react-dom/client';
import 'sanitize.css/sanitize.css';
import 'styles/global.scss';

import { RouterProvider } from '@tanstack/react-router';
import { router } from './tanstackRouter';

// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider';

// TanStack Query Provider
import { QueryProvider } from 'providers/QueryProvider';

// Training
import { TrainingProvider } from 'training/TrainingContext';

// MathJax
import { MathJaxProvider } from 'react-hook-mathjax';

// Import i18n messages
import { translationMessages } from './i18n';

// Suppress @hello-pangea/dnd / legacy DnD defaultProps warnings in development
if (process.env.NODE_ENV === 'development') {
  // Override console methods
  const originalWarn = console.warn;
  const originalError = console.error;
  const originalLog = console.log;

  // Function to check if message should be suppressed.
  // Only substring checks — no regex. A previous version included
  // `/defaultProps.*memo.*components/i.test(message)` here as a
  // catch-all, but that pattern has two greedy `.*` segments and
  // catastrophically backtracks on long inputs, throwing
  // "Stack overflow" from inside RegExp.test. The throw bubbled up
  // through the overridden console.error and produced confusing
  // "[e2e injectUserRecord] dispatch failed" noise during Playwright
  // runs (and would have been a real bug if any production-style
  // long message ever flowed through this path). The four
  // substring checks above already cover every variant of the
  // React-18 deprecation we've seen.
  const shouldSuppressMessage = (message) => {
    if (typeof message !== 'string') return false;
    return (
      message.includes('Support for defaultProps will be removed') ||
      message.includes('Connect(Droppable)') ||
      message.includes('defaultProps will be removed') ||
      message.includes('memo components') ||
      message.includes('react-beautiful-dnd') ||
      message.includes('hello-pangea') ||
      // react-quill@2.0.0 uses the deprecated React.findDOMNode API
      // internally. We can't fix the library; React 18 still keeps
      // findDOMNode working. TODO: migrate to `react-quill-new` (a
      // community fork that drops findDOMNode) — drop this clause
      // when that lands.
      message.includes('findDOMNode is deprecated')
    );
  };

  // Suppress all console methods that might show the warning
  console.warn = (...args) => {
    if (shouldSuppressMessage(args[0])) return;
    originalWarn.apply(console, args);
  };

  console.error = (...args) => {
    if (shouldSuppressMessage(args[0])) return;
    originalError.apply(console, args);
  };

  console.log = (...args) => {
    if (shouldSuppressMessage(args[0])) return;
    originalLog.apply(console, args);
  };

  // Try to suppress React's internal warnings
  const originalConsoleWarn = window.console.warn;
  window.console.warn = (...args) => {
    if (shouldSuppressMessage(args[0])) return;
    originalConsoleWarn.apply(window.console, args);
  };
}

// Global Opts: https://docs.mathjax.org/en/latest/options/
// SVG Specific Opts: http://docs.mathjax.org/en/latest/options/output/svg.html
const mathJaxOptions = {
  options: {
    enableMenu: false,
  },
};

const MOUNT_NODE = document.getElementById('app');
const root = createRoot(MOUNT_NODE);

const render = (messages) => {
  root.render(
    <QueryProvider>
      <LanguageProvider messages={messages}>
        <TrainingProvider>
          <MathJaxProvider options={mathJaxOptions} />
          <RouterProvider router={router} />
        </TrainingProvider>
      </LanguageProvider>
    </QueryProvider>
  );
};

if (import.meta.hot) {
  import.meta.hot.accept(['./i18n', './tanstackRouter'], () => {
    render(translationMessages);
  });
}

async function bootstrap() {
  // window.env is set synchronously by the blocking <script src="/environment.js">
  // in index.html, which runs before this module — no need to load it again here.

  // Chunked polyfill for browsers without Intl support
  if (!window.Intl) {
    await import('intl');
    await import('intl/locale-data/jsonp/en');
  }
  render(translationMessages);
}

bootstrap().catch((err) => {
  console.error(err);
  root.render(
    <div style={{ padding: '2rem', fontFamily: 'sans-serif', color: '#b71c1c' }}>
      <h1 style={{ fontSize: '1.25rem', marginTop: 0 }}>
        Unable to start the application
      </h1>
      <p style={{ marginBottom: 0 }}>
        {err?.message || String(err)}
      </p>
    </div>
  );
});
